import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Calendar, Users, DollarSign, MapPin, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BookingPage() {
  const { propertyId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingData, setBookingData] = useState({
    check_in_date: '',
    check_out_date: '',
    guests: 1,
    special_requests: '',
    accepts_liability_waiver: false
  });
  const [costBreakdown, setCostBreakdown] = useState(null);

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  useEffect(() => {
    if (bookingData.check_in_date && bookingData.check_out_date && property) {
      calculateCosts();
    }
  }, [bookingData.check_in_date, bookingData.check_out_date, property]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      toast.error('Failed to load property details');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const calculateCosts = () => {
    const checkIn = new Date(bookingData.check_in_date);
    const checkOut = new Date(bookingData.check_out_date);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    
    if (nights <= 0) {
      setCostBreakdown(null);
      return;
    }

    const subtotal = nights * property.price_per_night;
    const serviceFee = subtotal * 0.10; // 10% service fee (charged to property owner)
    const cleaningFee = 100; // $100 flat cleaning fee (paid by traveler)
    const totalAmount = subtotal + cleaningFee; // What traveler pays
    const ownerEarnings = subtotal - serviceFee; // What owner receives

    setCostBreakdown({
      nights,
      subtotal,
      serviceFee,
      cleaningFee,
      totalAmount,
      ownerEarnings
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'guests' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!costBreakdown) {
      toast.error('Please select valid check-in and check-out dates');
      return;
    }

    if (bookingData.guests > property.max_guests) {
      toast.error(`Maximum ${property.max_guests} guests allowed`);
      return;
    }

    const checkIn = new Date(bookingData.check_in_date);
    const checkOut = new Date(bookingData.check_out_date);
    
    if (checkIn < new Date()) {
      toast.error('Check-in date cannot be in the past');
      return;
    }

    if (!bookingData.accepts_liability_waiver) {
      toast.error('Please accept the liability disclaimer to complete booking');
      return;
    }

    setSubmitting(true);

    try {
      // Create booking first
      const booking = {
        property_id: propertyId,
        check_in_date: checkIn.toISOString(),
        check_out_date: checkOut.toISOString(),
        guests: bookingData.guests,
        special_requests: bookingData.special_requests || null
      };

      const bookingResponse = await axios.post(`${API}/bookings`, booking);
      const bookingResult = bookingResponse.data;
      
      toast.success('Booking created! Redirecting to payment...');
      
      // Create payment session
      const currentUrl = window.location.origin;
      const paymentRequest = {
        booking_id: bookingResult.id,
        success_url: `${currentUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${currentUrl}/book/${property.id}?cancelled=1`
      };
      
      const paymentResponse = await axios.post(`${API}/payments/checkout/session`, paymentRequest);
      
      // Redirect to Stripe checkout
      if (paymentResponse.data.url) {
        window.location.href = paymentResponse.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      const message = error.response?.data?.detail || 'Failed to create booking';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  // Calculate date restrictions for last-minute booking policy
  const today = new Date();
  
  // Allow same-day bookings
  const minCheckIn = today.toISOString().split('T')[0];
  
  // Maximum: 21 days from now
  const maxBookingDate = new Date(today);
  maxBookingDate.setDate(today.getDate() + 21);
  const maxCheckIn = maxBookingDate.toISOString().split('T')[0];
  
  // Minimum checkout is day after check-in
  const minCheckOut = bookingData.check_in_date 
    ? new Date(new Date(bookingData.check_in_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    : minCheckIn;

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        <button 
          onClick={() => navigate(-1)}
          className="btn btn-outline"
          style={{ 
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ArrowLeft size={20} />
          Back to Property
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
          {/* Property Summary */}
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            height: 'fit-content'
          }}>
            <h2 style={{ 
              fontSize: '1.75rem', 
              fontWeight: '600', 
              marginBottom: '24px',
              color: '#2d3748'
            }}>
              Property Details
            </h2>
            
            <div style={{ 
              height: '200px', 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '4rem',
              marginBottom: '24px'
            }}>
              🏠
            </div>
            
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '12px', color: '#2d3748' }}>
              {property.title}
            </h3>
            
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', color: '#718096' }}>
              <MapPin size={16} style={{ marginRight: '6px' }} />
              <span>{property.city}, {property.state}</span>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f7fafc', borderRadius: '8px' }}>
                <div style={{ fontWeight: '600', color: '#667eea' }}>{property.bedrooms}</div>
                <div style={{ fontSize: '0.875rem', color: '#718096' }}>Bedrooms</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f7fafc', borderRadius: '8px' }}>
                <div style={{ fontWeight: '600', color: '#667eea' }}>{property.bathrooms}</div>
                <div style={{ fontSize: '0.875rem', color: '#718096' }}>Bathrooms</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f7fafc', borderRadius: '8px' }}>
                <div style={{ fontWeight: '600', color: '#667eea' }}>{property.max_guests}</div>
                <div style={{ fontSize: '0.875rem', color: '#718096' }}>Max Guests</div>
              </div>
            </div>
            
            <div style={{ 
              textAlign: 'center',
              padding: '16px',
              background: '#f0f8f0',
              borderRadius: '8px',
              border: '1px solid #c6f6d5'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#667eea' }}>
                ${property.price_per_night}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>per night</div>
            </div>
          </div>

          {/* Booking Form */}
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 style={{ 
              fontSize: '1.75rem', 
              fontWeight: '600', 
              marginBottom: '24px',
              color: '#2d3748'
            }}>
              Book Your Stay
            </h2>

            {/* Last-Minute Quality Booking Policy Notice */}
            <div style={{
              background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h4 style={{ 
                color: '#1e40af', 
                fontWeight: '600', 
                marginBottom: '8px',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚡ Luxury Resort Timeshares
              </h4>
              <ul style={{ 
                color: '#1e40af', 
                fontSize: '0.9rem', 
                margin: '0',
                paddingLeft: '20px',
                lineHeight: '1.5'
              }}>
                <li><strong>Premier resort accommodations bookable same-day to 21 days in advance</strong></li>
                <li>Exclusively luxury timeshares at world-class resorts</li>
                <li>Perfect for spontaneous luxury escapes and unforgettable experiences</li>
              </ul>
            </div>

            {/* Important Booking Terms */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7, #fed7aa)',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h4 style={{ 
                color: '#92400e', 
                fontWeight: '600', 
                marginBottom: '8px',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚠️ Important Booking Terms
              </h4>
              <ul style={{ 
                color: '#92400e', 
                fontSize: '0.9rem', 
                margin: '0',
                paddingLeft: '20px',
                lineHeight: '1.5'
              }}>
                <li><strong>$100 resort preparation fee</strong> is required and added to every booking</li>
                <li>This fee is <strong>essential and COMPLETELY NON-REFUNDABLE</strong></li>
                <li><strong>STRICT cancellation policy:</strong> Cancellations within 24 hours of check-in are NON-REFUNDABLE (full amount charged)</li>
                <li>Resort preparation fee is never refunded under any circumstances</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    name="check_in_date"
                    value={bookingData.check_in_date}
                    onChange={handleInputChange}
                    min={minCheckIn}
                    max={maxCheckIn}
                    required
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Check-out Date
                  </label>
                  <input
                    type="date"
                    name="check_out_date"
                    value={bookingData.check_out_date}
                    onChange={handleInputChange}
                    min={minCheckOut}
                    required
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  Number of Guests
                </label>
                <select
                  name="guests"
                  value={bookingData.guests}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                  style={{ width: '100%' }}
                >
                  {Array.from({ length: property.max_guests }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} Guest{i + 1 !== 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  Special Requests (Optional)
                </label>
                <textarea
                  name="special_requests"
                  value={bookingData.special_requests}
                  onChange={handleInputChange}
                  placeholder="Any special requests or notes for the host..."
                  rows="4"
                  className="form-input"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              {/* Cost Breakdown */}
              {costBreakdown && (
                <div style={{ 
                  background: '#f7fafc', 
                  padding: '20px', 
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    marginBottom: '16px',
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <DollarSign size={20} />
                    Cost Breakdown ({costBreakdown.nights} night{costBreakdown.nights !== 1 ? 's' : ''})
                  </h4>
                  
                  <div style={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: '1.6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>${property.price_per_night} × {costBreakdown.nights} nights</span>
                      <span>${costBreakdown.subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>Service fee (10% charged to owner)</span>
                      <span>${costBreakdown.serviceFee.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Cleaning fee 
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#dc2626', 
                          fontWeight: '600',
                          background: '#fee2e2',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          REQUIRED
                        </span>
                      </span>
                      <span>${costBreakdown.cleaningFee}</span>
                    </div>
                    <div style={{ 
                      background: '#fef3c7', 
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      padding: '8px',
                      marginBottom: '12px',
                      fontSize: '0.75rem',
                      color: '#92400e'
                    }}>
                      ⚠️ The $100 cleaning fee is essential and non-refundable for each booking
                    </div>
                    <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#2d3748'
                    }}>
                      <span>Total Amount</span>
                      <span>${costBreakdown.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Liability Disclaimer */}
              <div style={{ 
                marginBottom: '24px',
                padding: '20px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <h4 style={{ 
                  color: '#dc2626', 
                  marginBottom: '12px',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  ⚠️ Important Liability Disclaimer
                </h4>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#7f1d1d',
                  lineHeight: '1.5',
                  marginBottom: '16px'
                }}>
                  By completing this booking, I acknowledge that ZypeeTravel is a marketplace platform only. 
                  ZypeeTravel is NOT responsible for any injuries, theft, damages, or incidents that may occur 
                  at the property. I understand that I am booking at my own risk and agree to hold ZypeeTravel 
                  harmless from any claims or damages.
                </p>
                
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    name="accepts_liability_waiver"
                    checked={bookingData.accepts_liability_waiver}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      width: '18px', 
                      height: '18px', 
                      marginTop: '2px',
                      accentColor: '#dc2626'
                    }}
                  />
                  <span style={{ color: '#7f1d1d' }}>
                    I have read and agree to the liability disclaimer above *
                  </span>
                </label>
              </div>

              <button 
                type="submit" 
                disabled={submitting || !costBreakdown || !bookingData.accepts_liability_waiver}
                className="btn btn-primary"
                style={{ 
                  width: '100%', 
                  padding: '14px',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Calendar size={20} />
                {submitting ? 'Creating Booking...' : 'Confirm Booking & Pay with Stripe'}
              </button>
            </form>

            {/* Alternative Payment Option */}
            <div style={{
              marginTop: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
              border: '2px solid #10b981',
              borderRadius: '12px'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#065f46',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💼 Alternative Payment Option
              </h4>
              <p style={{
                fontSize: '0.9rem',
                color: '#047857',
                marginBottom: '16px',
                lineHeight: '1.5'
              }}>
                Prefer to pay by invoice? Use our secure payment link for direct business payments.
              </p>
              <a
                href="https://32dbb5ac-e922-4f81-85ed-b1af921ca170.paylinks.godaddy.com/0550ea6d-5fe1-492b-b893-491"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#059669'}
                onMouseOut={(e) => e.target.style.background = '#10b981'}
              >
                <DollarSign size={18} />
                Pay by Invoice (GoDaddy)
              </a>
              <p style={{
                fontSize: '0.75rem',
                color: '#065f46',
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                Note: Invoice payments require manual confirmation. Please include your booking details.
              </p>
            </div>

            <div style={{ 
              marginTop: '20px',
              padding: '16px',
              background: '#f0f4f8',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#4a5568'
            }}>
              <strong>Note:</strong> Your booking will be pending until payment is processed. 
              Choose Stripe for instant confirmation or use the invoice payment link above.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingPage;